<template>
    <modal
        :adaptive="true"
        :class="{ 'mobile-item-modal': pivotY === 1 }"
        :clickToClose="!isLoading"
        :maxWidth="575"
        :pivotY="pivotY"
        :reset="true"
        :scrollable="true"
        height="auto"
        name="language-switcher-modal"
        transition="pop-out"
        width="575"
    >
        <div class="item-modal-content relative flex w-full flex-col">
            <div class="bg-secondary flex-auto overflow-y-auto">
                <div
                    class="flex items-center justify-between border border-b pl-4 py-2"
                >
                    <h2
                        class="text-base flex items-center font-heading text-heading font-bold"
                        >{{ $t('language.title') || 'Choose your language' }}
                        <language-icon class="ml-1" />
                    </h2>

                    <button
                        :disabled="isLoading"
                        class="text-primary bg-secondary rounded-full p-1.5"
                        @click="closeModal"
                    >
                        <close-icon class="h-5 w-5" />
                    </button>
                </div>
                <div class="px-1 pb-4 pt-2">
                    <ul>
                        <li
                            v-for="language in languages"
                            :key="language.code"
                            :class="{
                                'bg-primary':
                                    language.code.startsWith(selectedLang),
                                relative:
                                    isLoading && loadingCode === language.code,
                                'pointer-events-none opacity-50':
                                    isLoading && loadingCode !== language.code
                            }"
                            class="flex items-center cursor-pointer text-primary rounded-md border-b-1 hover:opacity-80 py-2 last:border-b-0"
                            @click="save(language.code)"
                        >
                            <div class="pl-2 text-lg">
                                {{ language.emoji }}
                            </div>
                            <div class="flex-1 ml-1.5 text-sm">
                                {{ language.name }}
                            </div>
                            <span
                                v-if="
                                    isLoading && loadingCode === language.code
                                "
                                class="spinner !text-primary"
                            ></span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import isMobile from '@/helpers/isMobileDevice';
import CloseIcon from '@/components/icons/CloseIcon';
import LanguageIcon from '@/components/icons/LanguageIcon';
import { languages, availableLanguages } from '@/util/languages';
import switchToLangOrCloserLang from '@/services/switchToLangOrCloserLang';

export default {
    components: { CloseIcon, LanguageIcon },
    name: 'language-switcher-modal',
    data() {
        return {
            languages: [],
            selectedLanguage: null,
            isLoading: false,
            loadingCode: null
        };
    },
    created() {
        const uniqueResults = new Set();

        for (const code of availableLanguages) {
            const foundItemFullCode = languages.find(
                item => item.code === code
            );

            if (foundItemFullCode) {
                uniqueResults.add(foundItemFullCode);
            } else {
                const foundItemPartialCode = languages.filter(item =>
                    item.code.startsWith(code.slice(0, 2))
                );

                foundItemPartialCode.forEach(item => uniqueResults.add(item));
            }
        }

        this.languages = Array.from(uniqueResults);
    },
    computed: {
        ...mapGetters({
            selectedLang: 'customer/lang',
            venueValue: 'venue/venueValue'
        }),
        pivotY() {
            return isMobile() ? 1 : 0.5;
        },
        venueId() {
            return this.venueValue('id');
        },
        accountId() {
            return this.venueValue('accountId');
        }
    },
    methods: {
        closeModal() {
            this.$modal.hide('language-switcher-modal');
        },
        async save(code) {
            if (this.isLoading) return;

            this.isLoading = true;
            this.loadingCode = code;

            try {
                await switchToLangOrCloserLang.handle(
                    code,
                    this.venueId,
                    this.accountId
                );
            } finally {
                this.isLoading = false;
                this.loadingCode = null;
                this.closeModal();
            }
        }
    }
};
</script>

<style scoped>
.spinner {
    opacity: 0.9;
    color: var(--color-text-primary, #000) !important;
}

.spinner:after {
    left: auto;
    right: 15px;
}
</style>
